import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SalesActions } from "@/components/crm/SalesActions";
import { ContractDialog } from "@/components/crm/ContractDialog";
import { RecordDrawer } from "@/components/crm/RecordDrawer";
import { SalesShell } from "@/components/crm/SalesShell";
import { BulkBar, SelectBox, useRowSelection } from "@/components/crm/BulkBar";
import { EmptyState, RenewalBadge, RiskBadge, SelectField, Stat } from "@/components/crm/parts";
import { contractSheets } from "@/lib/exports/crm";
import { contractVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import { useAccounts, useContracts, useDeleteRecord } from "@/lib/crm/useCrm";
import { RENEWAL_BANDS, daysUntil, gbp, isLiveContract, money, renewalBand, type Contract } from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/contracts")({
  head: () => ({
    meta: [
      { title: "Contracts — MyPriorities" },
      {
        name: "description",
        content:
          "The contract book: values, billing, inflation mechanisms, break clauses and renewal warning bands.",
      },
      { property: "og:title", content: "Contracts — MyPriorities" },
      {
        property: "og:description",
        content: "Never miss a renewal, uplift review or notice deadline again.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ContractsPage,
});

function ContractsPage() {
  return (
    <SalesShell
      title="Contracts"
      subtitle="Values, terms, uplift mechanisms and renewal warnings in one book."
    >
      {({ orgId, userId }) => <Contracts orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

function Contracts({ orgId, userId }: { orgId: string; userId: string }) {
  const contracts = useContracts(orgId);
  const accounts = useAccounts(orgId);
  const { ownerLabel, ownerOptions } = useOrgMembers(orgId);
  const remove = useDeleteRecord("crm_contracts");
  const selection = useRowSelection();

  const [search, setSearch] = useState("");
  const [band, setBand] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [detail, setDetail] = useState<Contract | null>(null);

  const { scope } = useSalesOwner();
  const accountRows = accounts.data ?? [];
  const accountName = (id: string | null) =>
    accountRows.find((a) => a.id === id)?.name ?? "No account";

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scope(contracts.data ?? [])
      .filter((c) => {
        if (band !== "all") {
          const days = daysUntil(c.end_date);
          if (days === null || days < 0 || days / 30.44 > Number(band)) return false;
        }
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          accountName(c.account_id).toLowerCase().includes(q) ||
          (c.product_name ?? "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.end_date ?? "9999").localeCompare(b.end_date ?? "9999"));
  }, [contracts.data, search, band, accountRows]);

  // One shared definition of a live contract, so this page, the overview and
  // the revenue plan can never disagree on contracted revenue.
  const live = rows.filter(isLiveContract);
  const annual = live.reduce((s, c) => s + money(c.annual_value), 0);
  const total = live.reduce((s, c) => s + money(c.total_value), 0);
  const superseded = rows.length - live.length;
  const atRisk = live
    .filter((c) => c.risk_status === "at_risk" || c.risk_status === "expected_loss")
    .reduce((s, c) => s + money(c.annual_value), 0);

  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="Annual value"
          value={gbp(annual)}
          hint={`${live.length} live${superseded ? ` · ${superseded} superseded` : ""}`}
        />
        <Stat label="Total contract value" value={gbp(total)} />
        <Stat label="At risk" value={gbp(atRisk)} />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-full sm:basis-64">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contracts, accounts or products"
          />
        </div>
        <div className="min-w-0 basis-full sm:basis-48">
        <SelectField
          label="Renewal window"
          value={band}
          onChange={setBand}
          options={[
            { value: "all", label: "All contracts" },
            ...RENEWAL_BANDS.map((b) => ({
              value: String(b),
              label: `Within ${b} ${b === 1 ? "month" : "months"}`,
            })),
          ]}
        />
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">

          <SalesActions
            exports={[
              {
                label: "Contract book",
                baseName: "contracts",
                sheets: () =>
                  contractSheets({ contracts: rows, accounts: accountRows, ownerLabel }),
              },
            ]}
            visual={() => contractVisual(rows, accountName)}
            sourceNote="From your contract book"
          />
          <Button
            className="shrink-0"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" /> New contract
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No contracts here"
          body="Add a contract to track value, uplift mechanisms, break clauses and renewal dates."
        />
      ) : (
        <>
        <BulkBar
          table="crm_contracts"
          noun="contract"
          ids={rows.map((r) => r.id)}
          selected={selection.selected}
          onReplace={selection.replace}
          onClear={selection.clear}
          blockedHint="They are still linked to opportunities or documents."
        />
        <ul className="grid gap-2">
          {rows.map((c) => (
            <li key={c.id} className="rounded-lg border border-border bg-card p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <SelectBox
                    checked={selection.isSelected(c.id)}
                    onChange={() => selection.toggle(c.id)}
                    label={`Select ${c.name}`}
                  />
                <div className="min-w-0">
                  <button
                    type="button"
                    className="text-left text-sm font-semibold hover:underline"
                    onClick={() => setDetail(c)}
                  >
                    {c.name}
                  </button>
                  <p className="text-[11px] text-muted-foreground">
                    {accountName(c.account_id)}
                    {c.product_name ? ` · ${c.product_name}` : ""} · Owner{" "}
                    {ownerLabel(c.owner_user_id)}
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {c.start_date ?? "—"} → {c.end_date ?? "—"} ·{" "}
                    {c.billing_frequency ?? "Billing not set"}
                    {c.rpi_linked ? " · RPI/CPI linked" : ""}
                    {c.break_clause ? ` · Break ${c.break_clause_date ?? "date TBC"}` : ""}
                    {c.auto_renewal ? " · Auto-renews" : ""}
                  </p>
                </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{gbp(c.annual_value, c.currency ?? "GBP")} p.a.</Badge>
                  {(c.lifecycle_status ?? "active") !== "active" ? (
                    <Badge variant="secondary">
                      {c.lifecycle_status === "superseded" ? "Superseded" : "Ended"}
                    </Badge>
                  ) : null}
                  <RenewalBadge endDate={c.end_date} />
                  <RiskBadge status={c.risk_status} />
                  {renewalBand(c.end_date) ? (
                    <Badge variant="secondary">{renewalBand(c.end_date)}-month band</Badge>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(c);
                      setDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete contract"
                    onClick={() =>
                      void remove
                        .mutateAsync(c.id)
                        .then(() => toast.success("Contract deleted"))
                        .catch(() =>
                          toast.error("That contract is still linked to an opportunity."),
                        )
                    }
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        </>
      )}

      <ContractDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={orgId}
        userId={userId}
        contract={editing}
        accounts={accountRows}
        ownerOptions={ownerOptions}
      />

      <RecordDrawer
        open={Boolean(detail)}
        onOpenChange={(v) => !v && setDetail(null)}
        orgId={orgId}
        userId={userId}
        kind="contract"
        recordId={detail?.id ?? null}
        contractId={detail?.id ?? null}
        revenueContract={detail}
        title={detail?.name ?? ""}
        subtitle={detail ? accountName(detail.account_id) : undefined}
      />
    </div>
  );
}
